export async function trackInitiateCheckout(
  opts: {
    orderId?: string | number;
    valueBRL?: number;
    currency?: string;
    email?: string;
    phone?: string;
    alsoPixel?: boolean;
  } = {}
) {
  if (typeof window === "undefined") return;
  const { fbp, fbc } = getFbParams();
  const flowId = getFlowId();
  // Dedupe guard: fire InitiateCheckout only once per flow in the session
  try {
    const key = "analytics_checkout_sent";
    // Include orderId in the fingerprint when available so a new order in the same flow can fire
    const fingerprint = `${flowId}|${
      opts.orderId ? String(opts.orderId) : "noorder"
    }`;
    const last = sessionStorage.getItem(key);
    if (last === fingerprint) {

      console.info("[Meta] InitiateCheckout ignorado (duplicado)", {
        flowId,
        orderId: opts.orderId,
      });
      return;
    }
    sessionStorage.setItem(key, fingerprint);
  } catch {}

  const ts = Date.now();
  const { cleanUrl, utm } = getCleanUrlAndUtm(window.location.href);
  // Use a stable event id for deduplication across Pixel and CAPI
  const orderPart = opts.orderId ? `order:${opts.orderId}` : "flow";
  const eventId = `${flowId}|checkout|${orderPart}`;

  // Use currency from opts or default to BRL
  const currency = (opts.currency || "BRL").toUpperCase();
  void trackFunnelEvent({
    eventName: "InitiateCheckout",
    eventId,
    flowId,
  });

  if (opts.alsoPixel) {
    const value = typeof opts.valueBRL === "number" ? opts.valueBRL : 0;
    sendPixel(
      "InitiateCheckout",
      {
        value,
        currency,
        contents: [{ id: "carta_canalizada", quantity: 1, item_price: value }],
        num_items: 1,
      },
      eventId
    );
  }
  return sendAdvertisingEventSafe({
    event_name: "InitiateCheckout",
    action_source: "website",
    event_source_url: cleanUrl,
    event_time: Math.floor(ts / 1000),
    event_id: eventId,
    user_data: {
      em: opts.email,
      ph: opts.phone,
      external_id: opts.orderId ? String(opts.orderId) : undefined,
      client_user_agent: navigator.userAgent,
      fbp,
      fbc,
    },
    custom_data: {
      currency,
      value: typeof opts.valueBRL === "number" ? opts.valueBRL : 0,
      content_name: "Carta Canalizada",
      content_category: "Serviço",
      content_ids: ["carta_canalizada"],
      contents: [
        {
          id: "carta_canalizada",
          quantity: 1,
          item_price: typeof opts.valueBRL === "number" ? opts.valueBRL : 0,
        },
      ],
      num_items: 1,
      order_id: opts.orderId ? String(opts.orderId) : undefined,
      status: "initiated",
      flow_id: flowId,
      utm_source: utm.utm_source,
      utm_medium: utm.utm_medium,
      utm_campaign: utm.utm_campaign,
      utm_term: utm.utm_term,
      utm_content: utm.utm_content,
      fbclid: utm.fbclid,
      gclid: utm.gclid,
    },
  });
}
import config from "@/config/app";
import { TarotApiService } from "./tarotApi";
import { sendTikTokEventSafe } from "./tiktokAnalytics";

// Use server-side proxy which injects Authorization from HttpOnly cookie
const PROXY_BASE = "/api/proxy";

type ActionSource =
  | "website"
  | "app"
  | "phone_call"
  | "chat"
  | "physical_store"
  | "system_generated"
  | "email"
  | "other";

type LandingPageVariant = "pt-BR" | "en-US" | "pt-PT";

export type ConversionEvent = {
  event_name: string;
  event_time?: number;
  action_source?: ActionSource;
  event_source_url?: string;
  event_id?: string;
  user_data?: {
    em?: string;
    ph?: string;
    external_id?: string;
    client_ip_address?: string;
    client_user_agent?: string;
    fbp?: string;
    fbc?: string;
  };
  custom_data?: {
    currency?: string;
    value?: number;
    content_name?: string;
    content_category?: string;
    content_ids?: string[];
    contents?: Array<Record<string, unknown>>;
    num_items?: number;
    status?: string;
    order_id?: string;
    // Funnel fields & UTMs (may be ignored by backend if not allowed)
    step?: number;
    step_label?: string;
    max_steps?: number;
    flow_id?: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_term?: string;
    utm_content?: string;
    fbclid?: string;
    ttclid?: string;
    gclid?: string;
  };
  test_event_code?: string;
};

// Minimal fbq typing
declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

function sendPixel(
  eventName: string,
  payload: Record<string, unknown>,
  eventId?: string,
  isCustom = false
) {
  if (typeof window === "undefined") return;
  // Respect feature flag for client-side Meta Pixel events
  if (!config.analytics?.pixelEnabled) {
    try {

      console.info(
        "[Meta] Pixel desabilitado via flag. Evento não enviado ao fbq:",
        eventName
      );
    } catch {}
    return;
  }
  const fbq = window.fbq;
  if (!fbq) return;
  try {
    if (isCustom) {
      fbq(
        "trackCustom",
        eventName,
        payload,
        eventId ? { eventID: eventId } : undefined
      );
    } else {
      fbq(
        "track",
        eventName,
        payload,
        eventId ? { eventID: eventId } : undefined
      );
    }
  } catch {}
}

// Basic validators/sanitizers for identifiers
function isValidEmail(email?: string): boolean {
  if (!email) return false;
  const e = String(email).trim();
  if (!e || e.length > 254) return false;
  const re = /^[^@\s]+@[^@\s]+\.[^@\s]+$/i;
  return re.test(e);
}

function digitsOnly(phone?: string): string {
  return (phone || "").replace(/\D/g, "");
}

function isValidPhone(phone?: string): boolean {
  const d = digitsOnly(phone);
  // Accept E.164-like lengths: 10 to 15 digits
  return d.length >= 10 && d.length <= 15;
}

// Hashing helpers (SHA-256 hex, lowercase)
async function sha256HexLower(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest("SHA-256", data);
  const bytes = Array.from(new Uint8Array(digest));
  return bytes.map((b) => b.toString(16).padStart(2, "0")).join("");
}

// Flow/session helpers
export function getFlowId(): string {
  if (typeof window === "undefined") return "no-window";
  try {
    const key = "analytics_flow_id";
    const existing = sessionStorage.getItem(key) || localStorage.getItem(key);
    if (existing) return existing;
    const id = `flow_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    sessionStorage.setItem(key, id);
    try {
      localStorage.setItem(key, id);
    } catch {}
    return id;
  } catch {
    return `flow_${Date.now()}`;
  }
}

function getSessionId(): string {
  if (typeof window === "undefined") return "no-window";
  try {
    const key = "analytics_session_id";
    const existing = sessionStorage.getItem(key);
    if (existing) return existing;
    const id = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    sessionStorage.setItem(key, id);
    return id;
  } catch {
    return `sess_${Date.now()}`;
  }
}

async function trackFunnelEvent(payload: {
  eventName: string;
  eventId?: string;
  flowId?: string;
  stepNumber?: number;
  stepLabel?: string;
  maxSteps?: number;
}) {
  if (typeof window === "undefined") return;

  const { cleanUrl, utm } = getCleanUrlAndUtm(window.location.href);
  const flowId = payload.flowId || getFlowId();
  const sessionId = getSessionId();
  const pageVariant = config.i18n.language || "pt-BR";
  const pathname = (() => {
    try {
      return new URL(window.location.href).pathname;
    } catch {
      return undefined;
    }
  })();

  const body = {
    event_name: payload.eventName,
    event_id: payload.eventId,
    page_variant: pageVariant,
    step_number: payload.stepNumber,
    step_label: payload.stepLabel,
    max_steps: payload.maxSteps,
    flow_id: flowId,
    session_id: sessionId,
    event_source_url: cleanUrl,
    pathname,
    utm_source: utm.utm_source,
    utm_medium: utm.utm_medium,
    utm_campaign: utm.utm_campaign,
    utm_term: utm.utm_term,
    utm_content: utm.utm_content,
    fbclid: utm.fbclid,
    gclid: utm.gclid,
  };

  const doPost = () =>
    fetch(`${PROXY_BASE}/api/funnel-metrics/events`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

  try {
    let res = await doPost();
    if (res.status === 401 || res.status === 403) {
      await TarotApiService.autoLogin();
      res = await doPost();
    }
    if (!res.ok) {

      console.warn("[Analytics] Funnel event não persistido", {
        status: res.status,
        eventName: payload.eventName,
        stepNumber: payload.stepNumber,
      });
    }
  } catch (err) {

    console.error("[Analytics] Erro de rede ao persistir funnel event", err);
  }
}

/** Sends diagnostic funnel events only to the internal metrics endpoint. */
export async function trackInternalFunnelEvent(
  eventName: string,
  opts: {
    stepNumber?: number;
    stepLabel?: string;
    maxSteps?: number;
  } = {}
) {
  if (typeof window === "undefined") return;

  const flowId = getFlowId();
  const eventId = `${flowId}|${eventName}`;

  try {
    const sentKey = "analytics_internal_diagnostic_events_sent";
    const sentRaw = sessionStorage.getItem(sentKey);
    const sent = sentRaw ? (JSON.parse(sentRaw) as string[]) : [];
    if (sent.includes(eventId)) return;
    sessionStorage.setItem(sentKey, JSON.stringify([...sent, eventId]));
  } catch (err) {

    console.error("[Analytics] Falha ao preparar evento interno", err);
  }

  await trackFunnelEvent({
    eventName,
    eventId,
    flowId,
    stepNumber: opts.stepNumber,
    stepLabel: opts.stepLabel,
    maxSteps: opts.maxSteps,
  });
}

// Remove tracking params from URL and return UTMs
function getCleanUrlAndUtm(href: string): {
  cleanUrl: string;
  utm: Record<string, string>;
} {
  try {
    const url = new URL(href);
    const params = url.searchParams;
    const utm: Record<string, string> = {};
    const keys = Array.from(params.keys());
    const trackingKeys = new Set([
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_term",
      "utm_content",
      "fbclid",
      "ttclid",
      "gclid",
      "msclkid",
      "twclid",
      "dclid",
    ]);
    for (const k of keys) {
      if (trackingKeys.has(k) || k.startsWith("utm_")) {
        utm[k] = params.get(k) || "";
        // Keep ttclid in the server event URL. TikTok uses it as a click
        // identifier, while the other tracking parameters remain stripped.
        if (k !== "ttclid") params.delete(k);
      }
    }
    // Fallback to cookies if utm empty
    if (Object.keys(utm).length === 0 && typeof document !== "undefined") {
      const maybe = [
        "utm_source",
        "utm_medium",
        "utm_campaign",
        "utm_term",
        "utm_content",
        "fbclid",
        "ttclid",
        "gclid",
      ];
      for (const k of maybe) {
        const v = readCookie(k);
        if (v) utm[k] = v;
      }
    }
    url.search = params.toString();
    return { cleanUrl: url.toString(), utm };
  } catch {
    return { cleanUrl: href, utm: {} };
  }
}

export async function sendMetaEvent(event: ConversionEvent) {
  // Only run in browser context
  if (typeof window === "undefined") return;

  // Start logging group only in development
  if (process.env.NODE_ENV !== "production") {
    try {

      console.group(
        `🔵 [Meta] sendMetaEvent iniciado - ${event.event_name || "SemNome"}`
      );
    } catch {}
  }

  // Respect feature flag for Conversions API (backend) sending
  if (!config.analytics?.capiEnabled) {
    try {

      console.warn(
        "❌ [Meta] CAPI desabilitado via flag. Evento não enviado ao backend:",
        event.event_name
      );
    } catch {}
    if (process.env.NODE_ENV !== "production") {
      try {

        console.groupEnd();
      } catch {}
    }
    return;
  }

  // Enrich missing fields for better matching
  const { fbp, fbc } = getFbParams();
  // Validate/sanitize user identifiers first
  const rawEm = event.user_data?.em;
  const rawPh = event.user_data?.ph;
  const emailValid = isValidEmail(rawEm);
  const phoneValid = isValidPhone(rawPh);
  const sanitizedEm = emailValid ? String(rawEm).trim() : undefined;
  const sanitizedPh = phoneValid ? digitsOnly(rawPh) : undefined;
  const rawExternalId = event.user_data?.external_id
    ? String(event.user_data.external_id).trim().toLowerCase()
    : undefined;

  // Log validation status in development
  if (process.env.NODE_ENV !== "production") {
    try {

      console.debug("[Meta] ✓ Validação de identificadores", {
        "E-mail válido": emailValid,
        "Telefone válido": phoneValid,
        "External ID presente": Boolean(rawExternalId),
        fbp: Boolean(fbp),
        fbc: Boolean(fbc),
      });
    } catch {}
  }

  // Hash PII on client to avoid sending raw identifiers
  const hashedEm = sanitizedEm
    ? await sha256HexLower(sanitizedEm.toLowerCase())
    : undefined;
  const hashedPh = sanitizedPh
    ? await sha256HexLower(sanitizedPh.toLowerCase())
    : undefined;
  const hashedExternalId = rawExternalId
    ? await sha256HexLower(rawExternalId)
    : undefined;
  // Note: fbp and fbc should NOT be hashed - they must be sent in plain text

  // Clean event_source_url and collect UTMs
  const { cleanUrl } = getCleanUrlAndUtm(
    event.event_source_url || window.location.href
  );

  const enriched: ConversionEvent = {
    ...event,
    event_source_url: cleanUrl,
    event_time: Math.floor(Date.now() / 1000),
    user_data: {
      client_user_agent: navigator.userAgent,
      ...event.user_data,
      // Apply hashed identifiers (drop invalid)
      ...(hashedEm ? { em: hashedEm } : { em: undefined }),
      ...(hashedPh ? { ph: hashedPh } : { ph: undefined }),
      ...(hashedExternalId
        ? { external_id: hashedExternalId }
        : { external_id: undefined }),
      // fbp and fbc must be sent in plain text (NOT hashed) per Meta requirements
      fbp: event.user_data?.fbp ?? fbp,
      fbc: event.user_data?.fbc ?? fbc,
    },
  };

  // Guard: require at least one identifier per Meta best practices
  const ids = [
    enriched.user_data?.em,
    enriched.user_data?.ph,
    enriched.user_data?.external_id,
    enriched.user_data?.fbp,
    enriched.user_data?.fbc,
  ].filter(Boolean);

  // Debug visibility (no PII): show which identifiers are present
  if (process.env.NODE_ENV !== "production") {
    try {

      console.debug("[Meta] ✓ Identificadores enriquecidos", {
        em: Boolean(enriched.user_data?.em),
        ph: Boolean(enriched.user_data?.ph),
        external_id: Boolean(enriched.user_data?.external_id),
        fbp: Boolean(enriched.user_data?.fbp),
        fbc: Boolean(enriched.user_data?.fbc),
        totalCount: ids.length,
        event: enriched.event_name,
        eventId: enriched.event_id,
      });
    } catch {}
  }

  if (ids.length === 0) {
    // Skip sending if we lack identifiers (avoid backend 400s)
    try {

      console.warn(
        "❌ [Meta] Evento não enviado: identificadores insuficientes"
      );
    } catch {}
    if (process.env.NODE_ENV !== "production") {
      try {

        console.groupEnd();
      } catch {}
    }
    return;
  }

  if (process.env.NODE_ENV !== "production") {
    try {

      console.debug(`✓ [Meta] Guard passou: ${ids.length} identificadores`);
    } catch {}
  }

  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  const payload = { data: [enriched] };

  // Append language query parameter for language-aware meta tracking
  const language = config.i18n.language || "pt-BR";
  const analyticsUrl = `${PROXY_BASE}/analytics/meta/events?language=${encodeURIComponent(
    language
  )}`;

  // Log pre-send info in development
  if (process.env.NODE_ENV !== "production") {
    try {
      const payloadSize = JSON.stringify(payload).length;

      console.debug("📤 [Meta] Preparado para enviar", {
        endpoint: analyticsUrl,
        product: enriched.event_name,
        language,
        payloadSize,
        identifierCount: ids.length,
      });
    } catch {}
  }

  const doPost = () =>
    fetch(analyticsUrl, {
      method: "POST",
      credentials: "include", // include HttpOnly cookie session
      headers,
      body: JSON.stringify(payload),
    });

  // Log request initiation in development
  if (process.env.NODE_ENV !== "production") {
    try {

      console.debug("🚀 [Meta] POST em andamento...");
    } catch {}
  }

  let res = await doPost();
  if (res.status === 401 || res.status === 403) {
    // Attempt to establish/refresh server session, then retry once
    try {
      await TarotApiService.autoLogin();
      res = await doPost();
    } catch {
      // ignore; will fall through to error handling below
    }
  }

  // Log response in development
  if (process.env.NODE_ENV !== "production") {
    try {

      console.debug("[Meta] Resposta recebida", {
        status: res.status,
        ok: res.ok,
        statusText: res.statusText,
      });
    } catch {}
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    try {

      console.error("❌ [Meta] Erro ao enviar evento", {
        status: res.status,
        detail: err.detail || err.message || "Desconhecido",
      });
    } catch {}
    if (process.env.NODE_ENV !== "production") {
      try {

        console.groupEnd();
      } catch {}
    }
    return {
      ok: false,
      status: res.status,
      detail: err.detail || err.message || `HTTP ${res.status}`,
    };
  }

  const json = await res.json();

  // Log success in development
  if (process.env.NODE_ENV !== "production") {
    try {

      console.info("✅ [Meta] Resposta sucesso:", json);

      console.info(
        `🎉 [Meta] Evento enviado com sucesso: ${enriched.event_name}`
      );
    } catch {}
  }

  if (process.env.NODE_ENV !== "production") {
    try {

      console.groupEnd();
    } catch {}
  }

  return json;
}

async function sendMetaEventSafe(event: ConversionEvent) {
  try {
    return await sendMetaEvent(event);
  } catch (error) {
    try {

      console.warn("[Meta] Event send failed (best-effort)", {
        eventName: event.event_name,
        error: error instanceof Error ? error.message : String(error),
      });
    } catch {}
    return { ok: false, error: error instanceof Error ? error.message : String(error) };
  }
}

async function sendAdvertisingEventSafe(event: ConversionEvent) {
  const params = (() => {
    try {
      return new URL(event.event_source_url || "").searchParams;
    } catch {
      return undefined;
    }
  })();
  const utmSource = (
    event.custom_data?.utm_source || params?.get("utm_source") || ""
  )
    .toLowerCase()
    .replace(/\s/g, "");
  const metaCampaign =
    Boolean(params?.get("fbclid") || event.custom_data?.fbclid) ||
    ["facebook", "instagram", "meta", "fbads", "igads"].some((source) =>
      utmSource.includes(source),
    );
  const tiktokCampaign =
    Boolean(params?.get("ttclid")) ||
    ["tiktok", "tik-tok", "bytedance"].some((source) =>
      utmSource.includes(source),
    );

  // Direct/organic visits can be measured by both. A paid campaign only goes
  // to its originating network; mixed click IDs are intentionally suppressed.
  if (!metaCampaign && !tiktokCampaign) {
    void sendTikTokEventSafe(event);
    return sendMetaEventSafe(event);
  }
  if (tiktokCampaign && !metaCampaign) {
    void sendTikTokEventSafe(event);
    return;
  }
  if (metaCampaign && !tiktokCampaign) {
    return sendMetaEventSafe(event);
  }
}

export function generateEventId(prefix: string) {
  // Simple event id with time and random suffix; customize if needed
  const rand = Math.random().toString(36).slice(2, 8);
  return `${prefix}_${Date.now()}_${rand}`;
}

function readCookie(name: string) {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(new RegExp("(^|; )" + name + "=([^;]*)"));
  return match ? decodeURIComponent(match[2]) : undefined;
}

function getFbParams() {
  if (typeof window === "undefined") return { fbp: undefined, fbc: undefined };
  const url = new URL(window.location.href);
  const fbclid = url.searchParams.get("fbclid");
  const fbp = readCookie("_fbp");
  let fbc = readCookie("_fbc");
  if (!fbc && fbclid) {
    // Build synthetic fbc from fbclid: fb.1.<timestamp_ms>.<fbclid> (use ms to match cookie spec)
    fbc = `fb.1.${Date.now()}.${fbclid}`;
    try {
      // Persist so próximos eventos reutilizam o mesmo valor (reduz discrepâncias Pixel x CAPI)
      document.cookie = `_fbc=${encodeURIComponent(fbc)}; path=/; max-age=${
        60 * 60 * 24 * 90
      }`; // 90 dias
    } catch {}
  }
  return { fbp, fbc };
}

export async function trackPageView(
  opts: { email?: string; phone?: string; alsoPixel?: boolean } = {}
) {
  if (typeof window === "undefined") return;

  if (process.env.NODE_ENV !== "production") {
    try {

      console.group("🔵 [Meta] trackPageView iniciado");
    } catch {}
  }

  const { fbp, fbc } = getFbParams();
  const flowId = getFlowId();
  // Short dedupe guard: prevents immediate duplicates (e.g. React StrictMode),
  // but still allows a new PageView when user re-enters the root later.
  try {
    const key = "analytics_pageview_last";
    const pathname =
      typeof window !== "undefined" ? window.location.pathname : "unknown";
    const now = Date.now();
    const lastRaw = sessionStorage.getItem(key);
    const last = lastRaw ? JSON.parse(lastRaw) : null;
    const isSamePathQuickRepeat =
      last &&
      last.pathname === pathname &&
      typeof last.ts === "number" &&
      now - last.ts < 3000;

    if (isSamePathQuickRepeat) {
      if (process.env.NODE_ENV !== "production") {
        try {

          console.info("[Meta] PageView ignorado (duplicado rápido)", {
            pathname,
          });
        } catch {}
      }
      return;
    }
    sessionStorage.setItem(key, JSON.stringify({ pathname, ts: now }));
  } catch {}

  const ts = Date.now();
  const { cleanUrl, utm } = getCleanUrlAndUtm(window.location.href);
  const eventId = `${flowId}|pageview|${ts}`;
  void trackFunnelEvent({
    eventName: "PageView",
    eventId,
    flowId,
  });

  if (process.env.NODE_ENV !== "production") {
    try {

      console.debug("[Meta] Flow ID e Event ID", {
        flowId,
        eventId,
      });
    } catch {}
  }

  if (opts.alsoPixel) {
    if (process.env.NODE_ENV !== "production") {
      try {

        console.debug("[Meta] Enviando também ao fbq (Pixel) para PageView");
      } catch {}
    }
    // PageView geralmente já é enviado automaticamente pelo Pixel base, então
    // só enviamos se explicitamente solicitado.
    sendPixel(
      "PageView",
      {
        flow_id: flowId,
      },
      eventId
    );
  }

  if (process.env.NODE_ENV !== "production") {
    try {

      console.debug("[Meta] Enviando Meta CAPI PageView...");
    } catch {}
  }

  const result = await sendAdvertisingEventSafe({
    event_name: "PageView",
    action_source: "website",
    event_source_url: cleanUrl,
    event_time: Math.floor(ts / 1000),
    event_id: eventId,
    user_data: {
      em: opts.email,
      ph: opts.phone,
      client_user_agent: navigator.userAgent,
      fbp,
      fbc,
    },
    custom_data: {
      flow_id: flowId,
      utm_source: utm.utm_source,
      utm_medium: utm.utm_medium,
      utm_campaign: utm.utm_campaign,
      utm_term: utm.utm_term,
      utm_content: utm.utm_content,
      fbclid: utm.fbclid,
      gclid: utm.gclid,
    },
  });

  if (process.env.NODE_ENV !== "production") {
    try {

      console.groupEnd();
    } catch {}
  }

  return result;
}

export async function trackLandingCtaClick(opts: {
  pageVariant: LandingPageVariant;
  sectionId: string;
  ctaId: string;
  targetUrl?: string;
}) {
  if (typeof window === "undefined") return;
  const { utm } = getCleanUrlAndUtm(window.location.href);
  const flowId = getFlowId();
  const sessionId = getSessionId();
  const body = {
    event_id: generateLandingCtaEventId(),
    page_variant: opts.pageVariant,
    section_id: opts.sectionId,
    cta_id: opts.ctaId,
    target_url: opts.targetUrl,
    flow_id: flowId,
    session_id: sessionId,
    utm_source: utm.utm_source,
    utm_medium: utm.utm_medium,
    utm_campaign: utm.utm_campaign,
    utm_term: utm.utm_term,
    utm_content: utm.utm_content,
    fbclid: utm.fbclid,
    gclid: utm.gclid,
  };

  // A full-page navigation immediately follows a CTA click. Persist first so a
  // cancelled request can be retried after the destination page loads.
  enqueuePendingLandingCtaClick(body);

  try {
    const res = await postLandingCtaClickWithAuthRetry(body, true);
    if (res.ok) removePendingLandingCtaClick(body.event_id);
  } catch {
    // Best-effort metrics only.
  }
}

type LandingCtaClickPayload = {
  event_id: string;
  page_variant: LandingPageVariant;
  section_id: string;
  cta_id: string;
  target_url?: string;
  flow_id: string;
  session_id: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  fbclid?: string;
  gclid?: string;
};

const PENDING_LANDING_CTA_CLICKS_KEY = "analytics_pending_landing_cta_clicks_v1";
const MAX_PENDING_LANDING_CTA_CLICKS = 20;

function generateLandingCtaEventId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `landing_cta_${crypto.randomUUID()}`;
  }
  return `landing_cta_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function readPendingLandingCtaClicks(): LandingCtaClickPayload[] {
  try {
    const raw = sessionStorage.getItem(PENDING_LANDING_CTA_CLICKS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (item): item is LandingCtaClickPayload =>
        Boolean(
          item &&
            typeof item === "object" &&
            typeof (item as LandingCtaClickPayload).event_id === "string" &&
            typeof (item as LandingCtaClickPayload).page_variant === "string" &&
            typeof (item as LandingCtaClickPayload).section_id === "string" &&
            typeof (item as LandingCtaClickPayload).cta_id === "string",
        ),
    );
  } catch {
    return [];
  }
}

function writePendingLandingCtaClicks(clicks: LandingCtaClickPayload[]): void {
  try {
    if (clicks.length === 0) {
      sessionStorage.removeItem(PENDING_LANDING_CTA_CLICKS_KEY);
      return;
    }
    sessionStorage.setItem(PENDING_LANDING_CTA_CLICKS_KEY, JSON.stringify(clicks));
  } catch {
    // Storage may be unavailable in restricted WebViews. keepalive remains a
    // best-effort delivery mechanism in that case.
  }
}

function enqueuePendingLandingCtaClick(click: LandingCtaClickPayload): void {
  const pending = readPendingLandingCtaClicks();
  writePendingLandingCtaClicks([
    ...pending.filter((item) => item.event_id !== click.event_id),
    click,
  ].slice(-MAX_PENDING_LANDING_CTA_CLICKS));
}

function removePendingLandingCtaClick(eventId: string): void {
  writePendingLandingCtaClicks(
    readPendingLandingCtaClicks().filter((click) => click.event_id !== eventId),
  );
}

function postLandingCtaClick(click: LandingCtaClickPayload, keepalive: boolean) {
  return fetch(`${PROXY_BASE}/api/landing-metrics/cta-clicks`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(click),
    // The payload is small and this keeps the request alive during the CTA
    // navigation in browsers that implement Fetch keepalive.
    keepalive,
  });
}

async function postLandingCtaClickWithAuthRetry(
  click: LandingCtaClickPayload,
  keepalive: boolean,
) {
  let response = await postLandingCtaClick(click, keepalive);
  if (response.status !== 401 && response.status !== 403) return response;

  await TarotApiService.autoLogin();
  response = await postLandingCtaClick(click, keepalive);
  return response;
}

/** Retries CTA clicks that were queued before a landing-page navigation. */
export async function flushPendingLandingCtaClicks(): Promise<void> {
  if (typeof window === "undefined") return;

  for (const click of readPendingLandingCtaClicks()) {
    try {
      const response = await postLandingCtaClickWithAuthRetry(click, false);
      if (response.ok) removePendingLandingCtaClick(click.event_id);
    } catch {
      // Keep it queued for a future page load in this browser session.
    }
  }
}

export function generateStepEventId(stepNumber: number): string {
  const flowId = getFlowId();
  return `${flowId}|step:${stepNumber}`;
}

const STEP_EVENT_NAME_BY_NUMBER: Record<number, string> = {
  1: "StepSobreVoce",
  2: "StepPessoaCanalizada",
  3: "StepContextoPergunta",
  4: "StepContato",
  5: "StepPagamento",
};

function getStepEventName(stepNumber: number): string {
  return STEP_EVENT_NAME_BY_NUMBER[stepNumber] || `StepProgress${stepNumber}`;
}

export async function trackStep(
  stepNumber: number,
  opts: {
    label?: string;
    email?: string;
    phone?: string;
    externalId?: string;
    maxSteps?: number;
    eventId?: string;
    alsoPixel?: boolean;
    sendInternal?: boolean;
    sendMeta?: boolean;
  } = {}
) {
  if (typeof window === "undefined") return;
  const { fbp, fbc } = getFbParams();
  const flowId = getFlowId();
  const ts = Date.now();
  const eventId = opts.eventId || `${flowId}|step:${stepNumber}`;
  const { cleanUrl, utm } = getCleanUrlAndUtm(window.location.href);
  const eventName = getStepEventName(stepNumber);
  const shouldSendInternal = opts.sendInternal ?? true;
  const shouldSendMeta = opts.sendMeta ?? true;

  // Dedupe guard for internal funnel event
  try {
    if (shouldSendInternal) {
      const fingerprint = `${flowId}|${stepNumber}`;
      const sentKey = "analytics_steps_sent_internal";
      const sentRaw = sessionStorage.getItem(sentKey);
      const sent = sentRaw ? (JSON.parse(sentRaw) as string[]) : [];
      if (sent.includes(fingerprint)) {

        console.info(`[Analytics] ${eventName} interno ignorado (duplicado)`, {
          stepNumber,
          flowId,
        });
      } else {
        sessionStorage.setItem(sentKey, JSON.stringify([...sent, fingerprint]));
        void trackFunnelEvent({
          eventName,
          eventId,
          flowId,
          stepNumber,
          stepLabel: opts.label,
          maxSteps: typeof opts.maxSteps === "number" ? opts.maxSteps : 8,
        });
      }
    }
  } catch (err) {

    console.error("[Analytics] Falha ao enviar evento interno de funil", err);
  }

  // Dedupe guard for Meta/CAPI + Pixel event
  try {
    if (shouldSendMeta) {
      const fingerprint = `${flowId}|${stepNumber}`;
      const sentKey = "analytics_steps_sent_meta";
      const sentRaw = sessionStorage.getItem(sentKey);
      const sent = sentRaw ? (JSON.parse(sentRaw) as string[]) : [];
      if (sent.includes(fingerprint)) {

        console.info(`[Meta] ${eventName} ignorado (duplicado)`, {
          stepNumber,
          flowId,
        });
        return;
      }
      sessionStorage.setItem(sentKey, JSON.stringify([...sent, fingerprint]));
    }
  } catch {}

  if (!shouldSendMeta) return;

  if (opts.alsoPixel) {
    sendPixel(
      eventName,
      {
        step: stepNumber,
        step_label: opts.label,
        max_steps: typeof opts.maxSteps === "number" ? opts.maxSteps : 8,
        flow_id: flowId,
        // Duplicamos em order_id para sobreviver ao schema do backend
        order_id: flowId,
      },
      eventId,
      true
    );
  }

  return sendAdvertisingEventSafe({
    event_name: eventName,
    action_source: "website",
    event_source_url: cleanUrl,
    event_time: Math.floor(ts / 1000),
    event_id: eventId,
    user_data: {
      em: opts.email,
      ph: opts.phone,
      external_id: opts.externalId,
      client_user_agent: navigator.userAgent,
      fbp,
      fbc,
    },
    custom_data: {
      content_name: "TarotFlow",
      step: stepNumber,
      step_label: opts.label,
      max_steps: typeof opts.maxSteps === "number" ? opts.maxSteps : 8,
      flow_id: flowId,
      // Para correlação no backend atual, enviamos o flowId em order_id
      order_id: flowId,
      utm_source: utm.utm_source,
      utm_medium: utm.utm_medium,
      utm_campaign: utm.utm_campaign,
      utm_term: utm.utm_term,
      utm_content: utm.utm_content,
      fbclid: utm.fbclid,
      gclid: utm.gclid,
    },
  });
}

export async function trackPurchaseSuccess(opts: {
  orderId: string | number;
  valueBRL?: number;
  currency?: string;
  email?: string;
  phone?: string;
  alsoPixel?: boolean;
}) {
  if (typeof window === "undefined") return;

  // Log detalhado para debug
  console.info("[Purchase] trackPurchaseSuccess called with:", {
    orderId: opts.orderId,
    valueBRL: opts.valueBRL,
    currency: opts.currency,
    hasEmail: Boolean(opts.email),
    hasPhone: Boolean(opts.phone),
    alsoPixel: opts.alsoPixel,
  });

  const { fbp, fbc } = getFbParams();
  const flowId = getFlowId();
  const ts = Date.now();
  const { cleanUrl, utm } = getCleanUrlAndUtm(window.location.href);
  // Ensure value is present for Purchase events (Meta requires custom_data.value)
  let value = typeof opts.valueBRL === "number" ? opts.valueBRL : undefined;
  if (typeof value === "undefined") {
    // Try to infer from URL (e.g., ?amount_cents=4500)
    try {
      const url = new URL(window.location.href);
      const centsStr = url.searchParams.get("amount_cents");
      const brlStr = url.searchParams.get("amount_brl");
      if (centsStr) {
        const parsed = parseFloat(centsStr);
        if (!Number.isNaN(parsed) && parsed > 0) value = parsed / 100;
      } else if (brlStr) {
        const parsed = parseFloat(brlStr.replace(",", "."));
        if (!Number.isNaN(parsed) && parsed > 0) value = parsed;
      }
    } catch {}
  }

  if (typeof value === "undefined") {

    console.warn("[Purchase] ❌ Purchase não enviado: valor ausente", {
      valueBRL_param: opts.valueBRL,
      url: window.location.href,
    });
    return;
  }

  // Use currency from opts or default to BRL
  const currency = (opts.currency || "BRL").toUpperCase();
  const purchaseEventId = `purchase_${opts.orderId}`;
  void trackFunnelEvent({
    eventName: "Purchase",
    eventId: purchaseEventId,
    flowId,
  });

  console.info("[Purchase] ✓ Valor validado, preparando envio:", {
    value,
    currency,
    orderId: opts.orderId,
  });

  if (opts.alsoPixel) {
    sendPixel(
      "Purchase",
      {
        value,
        currency,
        contents: [{ id: "carta_canalizada", quantity: 1, item_price: value }],
        num_items: 1,
        order_id: String(opts.orderId),
      },
      purchaseEventId
    );
  }

  return sendAdvertisingEventSafe({
    event_name: "Purchase",
    action_source: "website",
    event_source_url: cleanUrl,
    event_time: Math.floor(ts / 1000),
    event_id: purchaseEventId,
    user_data: {
      em: opts.email,
      ph: opts.phone,
      external_id: String(opts.orderId),
      client_user_agent: navigator.userAgent,
      fbp,
      fbc,
    },
    custom_data: {
      currency,
      value,
      content_name: "Carta Canalizada",
      content_category: "Tarot",
      content_ids: ["carta_canalizada"],
      contents: [
        {
          id: "carta_canalizada",
          quantity: 1,
          item_price: value,
        },
      ],
      num_items: 1,
      order_id: String(opts.orderId),
      status: "succeeded",
      flow_id: flowId,
      utm_source: utm.utm_source,
      utm_medium: utm.utm_medium,
      utm_campaign: utm.utm_campaign,
      utm_term: utm.utm_term,
      utm_content: utm.utm_content,
      fbclid: utm.fbclid,
      gclid: utm.gclid,
    },
  });
}
